import { createSignal, For, JSXElement, onMount, Show, useContext, createResource, createMemo } from 'solid-js';
import { isServer } from 'solid-js/web';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { Button } from '../../ui-components/button/button';
import { Heading } from '../../ui-components/heading/heading';
import { Checkbox } from '../../ui-components/inputs/checkbox/checkbox';
import { Link } from '../../ui-components/link/link';
import { Text } from '../../ui-components/text/text';
import { VerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { FormField, FormStoreKey } from './form-types';
import clsx from 'clsx';
import { validateCaptcha } from '../../tools/validate-captcha';
import { AngleDownIcon } from '../icons-library/angle-down';
import { validate } from './validation';
import { getCountryOptions, getPlaceholder, getUserTypeOptions } from './helpers';
import { formStore, resetFormStore, setError, setFormStoreField } from './registration-form-store';
import { useLocation } from '@solidjs/router';
import { logIn } from '../../tools/login-user';
import { FlexRow } from '../../ui-components/utility-style-components/flex';
import { atosGlobalCountrySlugToCountryCode } from '../../tools/countries-data';
import { ErrorCatcher } from '../../tools/error-catcher';
import { PrivacyPolicyModal } from '../privacy-policy-modal/privacy-policy-modal';
import { TermsAndConditions, UserTypeTerms } from '../../types/app-state';

type RegistrationFormProps = {
    createUser: (captchaToken: string, arrivedFromUrl?: string) => Promise<UserServiceResponse>;
    fields: FormField[];
    successMessageComponents: JSXElement;
    navigationComponents?: JSXElement;
    successIcon?: JSXElement;
    isAtosCareSite: boolean;
};

export type UserServiceResponse = {
    [key: string]: {
        status: string;
        message: string;
    };
};


export const RegistrationForm = (props: RegistrationFormProps) => {
    const {
        localize,
        privacyPolicyPage,
        recaptchaSiteKey,
        siteInfo,
        updateUser,
        translations,
        coursesListingPage,
        deploymentEnvironment,
        ssoBaseUrl,
        termsAndConditions,
        siteDomains,
    } = useContext(AppContext);
    const [success, setSuccess] = createSignal(false);
    const [arrivedFromUrl, setArrivedFromUrl] = createSignal('');
    const [loading,setLoading] = createSignal(false);
    const [showPrivacyPolicyModal, setShowPrivacyPolicyModal] = createSignal(false);
    const [showMarketingModal, setShowMarketingModal] = createSignal(false);

    // Get the siteId for the selected country
    // Matches via country code → site slug mapping instead of country name
    // If the current site is for the same country, prefer it (handles language variants like canada-fr)
    // Falls back to international site if no matching country found
    const getSelectedCountrySiteId = createMemo(() => {
        const countryCode = formStore.fields.country;
        if (countryCode) {
            // If current site matches the selected country, use it (respects language variants)
            const currentSiteCountryCode = siteInfo?.siteSlug ? atosGlobalCountrySlugToCountryCode[siteInfo.siteSlug] : undefined;
            if (currentSiteCountryCode === countryCode) {
                return siteInfo?.siteId;
            }
            const matchingSite = siteDomains.find(site =>
                atosGlobalCountrySlugToCountryCode[site.siteSlug] === countryCode
            );
            if (matchingSite) {
                return matchingSite.siteId;
            }
        }
        // Fall back to international site for countries without dedicated privacy policy
        const internationalSite = siteDomains.find(site => site.siteSlug === 'international');
        return internationalSite?.siteId || siteInfo?.siteId;
    });

    // Fetch terms and conditions for the selected country
    const [countryTerms] = createResource(
        getSelectedCountrySiteId,
        async (targetSiteId: string) => {
            // Skip fetching on server - will fetch on client after hydration
            if (isServer) return null;
            if (!targetSiteId) return null;
            try {
                const response = await fetch('/api-client/terms-and-conditions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ siteId: targetSiteId }),
                });
                const data = await response.json();
                if (data.success && data.termsAndConditions) {
                    return data.termsAndConditions as TermsAndConditions;
                }
                return null;
            } catch (error) {
                console.error('[RegistrationForm] Error fetching terms:', error);
                return null;
            }
        }
    );

    // Determine if user is HCP type - defaults to HCP unless Patient is explicitly selected
    // Since registration form only has HCP/Other options (no Patient), this will always be true
    // But we keep this logic for consistency with forms-tw and future-proofing
    const isHcpUserType = createMemo(() => {
        const userType = formStore.fields['user-type'];
        // Default to HCP (true) when nothing selected or when HCP/Other is selected
        // Only return false if 'Patient' is explicitly selected
        return userType !== 'Patient';
    });

    // Get effective terms - use fetched country terms or fall back to context,
    // then select HCP or Patient specific terms based on user-type
    const getEffectiveTerms = (): UserTypeTerms => {
        const terms = countryTerms() || termsAndConditions;
        if (!terms) {
            return { privacyPolicy: { title: '', checkboxText: '', content: '' }, marketingConsent: { title: '', checkboxText: '', content: '' } };
        }

        // If new structure with hcp/patient exists, use it based on user type
        if (terms.hcp && terms.patient) {
            return isHcpUserType() ? terms.hcp : terms.patient;
        }

        // Backwards compatibility: use flat structure
        return {
            privacyPolicy: terms.privacyPolicy,
            marketingConsent: terms.marketingConsent,
        };
    };

    // Fetch consent type for the selected country from Global Forms
    // Returns both hcpConsentType and patientConsentType for user-type based consent
    const [countryConsentSettings] = createResource(
        () => formStore.fields.country, // Track country code changes
        async (countryCode: string) => {
            // Skip fetching on server - will fetch on client after hydration
            if (isServer) return { hcpConsentType: 'single', patientConsentType: 'single' };
            if (!countryCode) return { hcpConsentType: 'single', patientConsentType: 'single' };
            try {
                const response = await fetch('/api-client/country-consent-type', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ countryCode }),
                });
                const data = await response.json();
                return {
                    hcpConsentType: data.hcpConsentType || 'single',
                    patientConsentType: data.patientConsentType || 'single',
                };
            } catch (error) {
                console.error('[RegistrationForm] Error fetching consent type:', error);
                return { hcpConsentType: 'single', patientConsentType: 'single' };
            }
        }
    );

    // Check if double consent is required for selected country and user type
    // Uses patientConsentType for Patient users, hcpConsentType for everyone else (HCP, Other)
    const isDoubleConsent = createMemo(() => {
        const settings = countryConsentSettings();
        if (!settings) return false;
        // Since registration form doesn't have Patient option, always use HCP consent type
        // But we keep the logic consistent with forms-tw for future-proofing
        return isHcpUserType()
            ? settings.hcpConsentType === 'double'
            : settings.patientConsentType === 'double';
    });

    // Check if the new Terms & Conditions ACF is configured (has actual content)
    // If not, we fall back to the old format (checkbox + link to privacy policy page)
    const isTermsConfigured = createMemo(() => {
        const terms = termsAndConditions;
        // Check if privacy policy content exists (the main indicator that ACF is configured)
        return !!(terms?.privacyPolicy?.content && terms.privacyPolicy.content.trim() !== '');
    });

    // Check if marketing consent content is configured
    // If not, we only show privacy policy link in single consent mode
    const hasMarketingContent = createMemo(() => {
        const terms = getEffectiveTerms();
        return !!(terms?.marketingConsent?.content && terms.marketingConsent.content.trim() !== '');
    });

    // Reactive memos for terms text - these will update when countryTerms changes
    const privacyCheckboxText = createMemo(() =>
        getEffectiveTerms()?.privacyPolicy?.checkboxText || localize('agree-to-terms', 'I have read and agree to the')
    );
    const privacyLinkText = createMemo(() =>
        getEffectiveTerms()?.privacyPolicy?.title || localize('privacy-policy', 'Privacy Policy')
    );
    const marketingCheckboxText = createMemo(() =>
        getEffectiveTerms()?.marketingConsent?.checkboxText || localize('agree-to-marketing', 'I agree to receive')
    );
    const marketingLinkText = createMemo(() =>
        getEffectiveTerms()?.marketingConsent?.title || localize('marketing-communications', 'marketing communications')
    );

    const location = useLocation() as { state?: { fromUrl?: string } };

    onMount(() => {
        if (location.state?.fromUrl) {
            setArrivedFromUrl(location.state.fromUrl);
            delete location.state.fromUrl;   
        }

        if (!document.querySelector('#grecaptcha')) {
            const scriptTag = document.createElement('script');
            scriptTag.type = 'text/javascript';
            scriptTag.id = 'grecaptcha';
            scriptTag.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
            document.head.appendChild(scriptTag);
        }
        
        // Do not set a default country for atos-care
        const currentSelectCountryCode = siteInfo.siteType === 'atos-care' 
            ? undefined
            : atosGlobalCountrySlugToCountryCode[ siteInfo.siteSlug as keyof typeof atosGlobalCountrySlugToCountryCode ];

        if (currentSelectCountryCode) {
            setFormStoreField({ country: currentSelectCountryCode });
        }
    });

    const isBusinessLead = () => formStore.fields[ 'user-type' ] === 'HCP' || formStore.fields[ 'user-type' ] === 'Other';

    const submit = async (e: any) => {
        e.preventDefault();

        const extraFields = [];

        if (isBusinessLead()) {
            extraFields.push({
                name: 'Company',
                slug: 'company',
                required: true,
            });
        }
        const validForm = validate({ fields: [...props.fields, ...extraFields], localize });

        if (!validForm) return;

        setLoading(true);

        try {            
            const captchaToken = await validateCaptcha('register_user', recaptchaSiteKey);

            if (!captchaToken) throw new Error('Captcha validation failed - probably a bot.');

            const response: UserServiceResponse = await props.createUser(captchaToken, arrivedFromUrl());
            
            const { status, message } = siteInfo.siteType === 'atos-care' ? response.createCareUser : response.createUser;
            if (status !== 'OK') throw new Error(message);

            setLoading(false);
            setSuccess(true);

            if (siteInfo.siteType === 'atos-care') {
                await handleLogin();
            }

            resetFormStore();
        } catch (e: any) {
            setLoading(false);
            console.log('Error submitting form: ', e);
                    
            if (e.message === 'USRE') {
                setError(localize('email-already-in-use', 'A user with that email already exists'));
            } else if (e.message === 'invalid-showpad-code') {
                setError(localize('invalid-showpad-code-registration', 'Invalid e-learning code provided - try again or leave empty in order to continue'));
            } else {
                setError(localize('form-submit-error', 'Error submitting the form, please try again later'));
            }
        }
    };

    const handleLogin = async () => {
        const args = {
            email: formStore.fields.email,
            password: formStore.fields.password,
        };

        const { success, message } = await logIn(args, updateUser, recaptchaSiteKey, deploymentEnvironment,  ssoBaseUrl);

        if (!success) {
            if (message === 'invalid-credentials') {
                setError(localize('invalid-credentials', 'Invalid credentials'));
            }
        }
    };

    const CountrySelect = ({ field }: { field: FormField }) => (
        <div>
            <label class="block text-[1.7rem] leading-[2.4rem] tracking-[0] font-atos-light font-normal text-darkGray mt-[0.9rem] mb-[0.8rem]" for="country-select" id="country-select-label">
                {`${localize('country', 'Country')}*`}
            </label>

            <div class="relative">
                <select
                    class={clsx(
                        'w-full text-normal font-atos-regular rounded-[5px] p-4 bg-white cursor-pointer appearance-none border border-solid',
                        '[&_option]:text-black',
                        'border-[#707070]',
                        formStore.fields['country'] === '' ? 'text-[#707070]' : 'text-black'
                    )}
                    id="country-select"
                    value={formStore.fields[ 'country' ]}
                    name='country'
                    autocomplete={field.autocomplete}
                    onChange={(e: any) => setFormStoreField({ country: e.target.value })}
                    aria-labelledby="country-select-label"
                    aria-describedby="desc-country"
                >
                    {getCountryOptions({
                        localize,
                        translations,
                        currentSelectCountryCode: formStore.fields[ 'country' ],
                        isAtosCare: siteInfo.siteType === 'atos-care'
                    })}
                </select>
                <div class="absolute right-[1.8rem] top-0 bottom-0 h-[1.8rem] pointer-events-none m-auto">
                    <AngleDownIcon height={1.2} width={1.2} min-width="1.2" min-height="1.2" fill="black" ariaHidden={true} />
                </div>
            </div>

            <span id="desc-country" style={{ display: 'none' }}>
                {localize('country', 'Country')}
            </span>
        </div>
    );

    const UserTypeSelect = ({ field }: { field: FormField; }) => (
        <div>
            <label class="block text-[1.7rem] leading-[2.4rem] tracking-[0] font-atos-light font-normal text-darkGray mt-[0.9rem] mb-[0.8rem]" for="user-type-select" id="user-type-select-label">
                {`${localize('user-type', 'User type')}${field.required ? '*' : ''}`}
            </label>

            <div class="relative">
                <select
                    class={clsx(
                        'w-full text-normal font-atos-regular rounded-[5px] p-4 bg-white cursor-pointer appearance-none border border-solid',
                        '[&_option]:text-black',
                        'border-[#707070]',
                        formStore.fields['user-type'] === '' ? 'text-[#707070]' : 'text-black'
                    )}
                    id="user-type-select"
                    value={formStore.fields[ 'user-type' ]}
                    name='user-type'
                    autocomplete={field.autocomplete}
                    onChange={(e: any) => setFormStoreField({ 'user-type': e.target.value })}
                    aria-labelledby="user-type-select-label"
                    aria-describedby="desc-user-type"
                >
                    {getUserTypeOptions(localize, translations)}
                </select>
                <div class="absolute right-[1.8rem] top-0 bottom-0 h-[1.8rem] pointer-events-none m-auto">
                    <AngleDownIcon height={1.2} width={1.2} min-width="1.2" min-height="1.2" fill="black" ariaHidden={true} />
                </div>
            </div>

            <span id="desc-user-type" style={{ display: 'none' }}>
                {localize('user-type', 'User type')}
            </span>
        </div>
    );

    const CareGiverCheckbox = () => (
        <div class="flex flex-wrap flex-row items-center gap-4">
            <Checkbox
                id="is-caregiver-checkbox"
                isChecked={formStore.fields[ 'is-caregiver' ]}
                whenClicked={() => {
                    setError('');
                    setFormStoreField({ 'is-caregiver': !formStore.fields[ 'is-caregiver' ] });
                }}
                value='is-caregiver'
                name={localize('is-caregiver-registration-question', 'Are you a caregiver?')}
                aria-describedby="desc-is-caregiver"
                aria-label={localize('is-caregiver-registration-question', 'Are you a caregiver?')}
            />

            <span id="desc-is-caregiver" style={{ display: 'none' }}>
                {localize('is-caregiver-registration-question', 'Are you a caregiver?')}
            </span>
        </div>
    );

    const CompanyField = () => (
        <div>
            <input class="w-full text-normal font-atos-regular rounded-[5px] p-4 border border-solid border-[#707070]"
                id="company-field"
                value={formStore.fields[ 'company' ]}
                placeholder={getPlaceholder({ name: 'Company', slug: 'company', required: true }, localize)}
                onChange={(e) => setFormStoreField({ [ 'company' ]: e.currentTarget.value })}
                onFocus={() => setError('')}
                type='text'
                name={'company'}
                required={true}
                aria-label={localize('company', 'Company')}
                aria-describedby="desc-company"
            />

            <span id="desc-company" style={{ display: 'none' }}>
                {localize('company', 'Company')}
            </span>
        </div>
    );

    const PatientField = ({ value, field }: { value: string; field: FormField }) => (
        <div style={field.autoWidth
            ? {
                width: 'auto',
                'flex-grow': '1'
            } : {
                width: '100%'
            }}
        >
            <input class="w-full text-normal font-atos-regular rounded-[5px] p-4 border border-solid border-[#707070]"
                id={`patient-field-${field.slug}`}
                value={value}
                placeholder={getPlaceholder(field, localize)}
                onChange={(e) => setFormStoreField({ [ field.slug ]: e.currentTarget.value })}
                onFocus={() => setError('')}
                type={field.type ? field.type : 'text'}
                name={field.slug}
                aria-label={getPlaceholder(field, localize)}
                aria-describedby={`desc-${field.slug}`}
            />

            <span id={`desc-${field.slug}`} style={{ display: 'none' }}>
                {getPlaceholder(field, localize)}
            </span>
        </div>
    );

    const PasswordField = () => (
        <div>
            <input class="w-full text-normal font-atos-regular rounded-[5px] p-4 border border-solid border-[#707070]"
                id="password-field"
                value={formStore.fields.password}
                name='password'
                autocomplete='new-password'
                placeholder={localize('password', 'Password')}
                type="password"
                onChange={(e) => setFormStoreField({ password: e.currentTarget.value })}
                onFocus={() => setError('')}
                aria-label={localize('password', 'Password')}
                aria-describedby="desc-password"
            />

            <span id="desc-password" style={{ display: 'none' }}>
                {localize('password', 'Password')}
            </span>
        </div>
    );

    const VerifyPasswordField = () => (
        <div>
            <input class="w-full text-normal font-atos-regular rounded-[5px] p-4 border border-solid border-[#707070]"
                id="verify-password-field"
                value={formStore.fields.verifyPassword}
                placeholder={localize('verify-password', 'Verify Password')}
                name='verify-password'
                autocomplete='new-password'
                type="password"
                onChange={(e) => setFormStoreField({ verifyPassword: e.currentTarget.value })}
                onFocus={() => setError('')}
                aria-label={localize('verify-password', 'Verify Password')}
                aria-describedby="desc-verify-password"
            />

            <span id="desc-verify-password" style={{ display: 'none' }}>
                {localize('verify-password', 'Verify Password')}
            </span>
        </div>
    );

    const ConsentCheckbox = () => {
        // If Terms & Conditions ACF is not configured, show the old format
        if (!isTermsConfigured()) {
            return (
                <div class="flex flex-wrap flex-row items-center gap-4">
                    <Checkbox
                        id='privacy-policy-consent'
                        isChecked={formStore.fields.consent}
                        whenClicked={() => {
                            setError('');
                            setFormStoreField({ consent: !formStore.fields['consent'] });
                        }}
                        value='privacy-policy-consent'
                        name={localize('agree-to-terms', 'I agree to the terms and conditions')}
                        aria-label={localize('agree-to-terms', 'I agree to the terms and conditions')}
                        aria-describedby="desc-privacy-policy-consent"
                    />
                    <Link noBlockSpacing={true} label={localize('read-more-here', 'Read more here')} url={privacyPolicyPage || '/'} />
                    <span id="desc-privacy-policy-consent" style={{ display: 'none' }}>
                        {localize('agree-to-terms', 'I agree to the terms and conditions')}
                    </span>
                </div>
            );
        }

        return (
            <div class="flex flex-wrap flex-row items-center gap-4">
                {/* Single Consent Mode: One checkbox with both privacy and marketing links inline */}
                <Show when={!isDoubleConsent()}>
                    <div class="flex flex-row items-start gap-cvi-4">
                        <div class="flex-shrink-0">
                            <Checkbox
                                id='privacy-policy-consent'
                                isChecked={formStore.fields.consent}
                                whenClicked={() => {
                                    setError('');
                                    setFormStoreField({ consent: !formStore.fields['consent'] });
                                }}
                                value='privacy-policy-consent'
                                name=""
                                aria-label={hasMarketingContent()
                                    ? `${privacyCheckboxText()} ${privacyLinkText()} ${localize('and', 'and')} ${marketingLinkText()}`
                                    : `${privacyCheckboxText()} ${privacyLinkText()}`
                                }
                                aria-describedby="desc-privacy-policy-consent"
                            />
                        </div>
                        <span class="py-cvi-4 label-md font-cvi-regular text-cvi-fg-default">
                            {privacyCheckboxText()}&nbsp;<button
                                type="button"
                                class="text-cvi-fg-link font-cvi-medium hover:underline cursor-pointer"
                                onClick={() => setShowPrivacyPolicyModal(true)}
                            >{privacyLinkText()}</button>
                            <Show when={hasMarketingContent()}>
                                &nbsp;{localize('and', 'and')}&nbsp;<button
                                    type="button"
                                    class="text-cvi-fg-link font-cvi-medium hover:underline cursor-pointer"
                                    onClick={() => setShowMarketingModal(true)}
                                >{marketingLinkText()}</button>
                            </Show>
                        </span>
                    </div>

                    <span id="desc-privacy-policy-consent" style={{ display: 'none' }}>
                        {hasMarketingContent()
                            ? `${privacyCheckboxText()} ${privacyLinkText()} ${localize('and', 'and')} ${marketingLinkText()}`
                            : `${privacyCheckboxText()} ${privacyLinkText()}`
                        }
                    </span>
                </Show>

                {/* Double Consent Mode: Two separate checkboxes stacked vertically */}
                <Show when={isDoubleConsent()}>
                    <div class="flex flex-col gap-[1rem]">
                        {/* Privacy Policy Consent (required) */}
                        <div class="flex flex-row items-start gap-cvi-4">
                            <div class="flex-shrink-0">
                                <Checkbox
                                    id='privacy-policy-consent-double'
                                    isChecked={formStore.fields.consent}
                                    whenClicked={() => {
                                        setError('');
                                        setFormStoreField({ consent: !formStore.fields['consent'] });
                                    }}
                                    value='privacy-policy-consent'
                                    name=""
                                    aria-label={`${privacyCheckboxText()} ${privacyLinkText()}`}
                                    aria-describedby="desc-privacy-policy-consent-double"
                                />
                            </div>
                            <span class="py-cvi-4 label-md font-cvi-regular text-cvi-fg-default">
                                {privacyCheckboxText()}&nbsp;<button
                                    type="button"
                                    class="text-cvi-fg-link font-cvi-medium hover:underline cursor-pointer"
                                    onClick={() => setShowPrivacyPolicyModal(true)}
                                >{privacyLinkText()}</button>
                            </span>
                        </div>

                        <span id="desc-privacy-policy-consent-double" style={{ display: 'none' }}>
                            {`${privacyCheckboxText()} ${privacyLinkText()}`}
                        </span>

                        {/* Marketing Consent (optional) */}
                        <div class="flex flex-row items-start gap-cvi-4">
                            <div class="flex-shrink-0">
                                <Checkbox
                                    id='marketing-consent'
                                    isChecked={formStore.fields.marketingConsent as boolean}
                                    whenClicked={() => {
                                        setFormStoreField({ marketingConsent: !formStore.fields['marketingConsent'] });
                                    }}
                                    value='marketing-consent'
                                    name=""
                                    aria-label={`${marketingCheckboxText()} ${marketingLinkText()}`}
                                    aria-describedby="desc-marketing-consent"
                                />
                            </div>
                            <span class="py-cvi-4 label-md font-cvi-regular text-cvi-fg-default">
                                {marketingCheckboxText()}&nbsp;<button
                                    type="button"
                                    class="text-cvi-fg-link font-cvi-medium hover:underline cursor-pointer"
                                    onClick={() => setShowMarketingModal(true)}
                                >{marketingLinkText()}</button>
                            </span>
                        </div>

                        <span id="desc-marketing-consent" style={{ display: 'none' }}>
                            {`${marketingCheckboxText()} ${marketingLinkText()}`}
                        </span>
                    </div>
                </Show>
            </div>
        );
    };

    const renderSuccessMessage = () => {
        return (
            <div class="p-l tablet:p-xxxl flex flex-col items-center bg-white rounded-[var(--radius-large)]">
                <div class="mb-s w-[80px] h-[80px] tablet:w-[100px] tablet:h-[100px]">
                    {props.successIcon}
                </div>

                <div class="mb-s mt-s">
                    {props.successMessageComponents}
                </div>

                <Show when={props.isAtosCareSite && arrivedFromUrl()}>
                    <FlexRow justifyContent='center'>
                        <Link
                            url={arrivedFromUrl()}
                            label={localize('registration-continue-where-you-left-off', 'Continue where you left off')}
                            plainStyle={true}
                            arrowDirection={'left'}
                            arrowPosition='left'
                            noBlockSpacing
                        />
                    </FlexRow>
                </Show>

                <VerticalSpace size={2} />

                {props.navigationComponents ? props.navigationComponents : null}
            </div>
        );
    };


    return (
        <ErrorCatcher componentName='Forms'>

            <Show when={success()}>
                {renderSuccessMessage()}
            </Show>

            <form class="flex flex-col gap-4 p-4 w-full max-w-[65rem]">
                <Show when={privacyPolicyPage && privacyPolicyPage !== '' }>
                    <Show when={!success()}>
                        <Heading tag="h2">{localize('user-information', 'User information')}</Heading>
                        <For each={props.fields}>
                            {(field: FormField) => {
                                if (field.slug === 'country') {
                                    return <CountrySelect field={field} />;
                                } else if (field.slug === 'user-type') {
                                    return <UserTypeSelect field={field} />;
                                } else if (field.slug === 'is-caregiver') {
                                    return <CareGiverCheckbox />;
                                } else {
                                    if (field.slug.includes('patient')) return null;

                                    if (field.slug === 'showpadCode' && !coursesListingPage.url) {
                                        return null;
                                    }

                                    const value = formStore.fields[field.slug as FormStoreKey];
                                    if (typeof value !== 'string') return null;

                                    return (
                                        <div>
                                            <input class="w-full text-normal font-atos-regular rounded-[5px] p-4 border border-solid border-[#707070]"
                                                id={`field-${field.slug}`}
                                                value={value}
                                                placeholder={getPlaceholder(field, localize)}
                                                onChange={(e) => setFormStoreField({ [ field.slug ]: e.currentTarget.value })}
                                                onFocus={() => setError('')}
                                                type={field.type ? field.type : 'text'}
                                                name={field.slug}
                                                autocomplete={field.autocomplete}
                                                required={field.required}
                                                aria-label={getPlaceholder(field, localize).replace('*', '')}
                                                aria-describedby={`desc-${field.slug}`}
                                            />
                                            <span id={`desc-${field.slug}`} style={{ display: 'none' }}>
                                                {getPlaceholder(field, localize).replace('*', '')}
                                            </span>
                                        </div>
                                    );
                                }
                            }}
                        </For>
                        <Show when={isBusinessLead()}>
                            <CompanyField />
                        </Show>

                        <Show when={formStore.fields[ 'is-caregiver' ]}>
                            <VerticalSpace size={0.5} />
                            <Heading tag="h2">
                                {localize('patient-information', 'Patient information')}
                            </Heading>
                            <div class="flex flex-wrap gap-4">
                                <For each={props.fields}>
                                    {(field: FormField) => {
                                        const value = formStore.fields[ field.slug as FormStoreKey ];
                                        if (typeof value !== 'string') return null;

                                        if (field.slug.includes('patient')) {
                                            return <PatientField value={value} field={field} />;
                                        }
                                    }}
                                </For>
                            </div>
                        </Show>
                        <VerticalSpace size={0.5} />
                        <Heading tag="h2">{localize('password', 'Password')}</Heading>

                        <PasswordField />

                        <VerifyPasswordField />

                        <Text noBlockSpacing={true} fontSize='small' fontStyle='italic' color='darkGray'>{`*${localize('required-fields', 'required fields')}`}</Text>
                        <VerticalSpace size={0.5} />

                        <ConsentCheckbox />
                    </Show>

                    <Show when={formStore.error}>
                        <div class="text-primary text-[16px] font-atos-regular mb-4">{formStore.error}</div>
                    </Show>

                    <Show when={loading()}>
                        <LoadingSpinner />
                    </Show>
                    <Show when={!success()}>
                        <Button 
                            type="submit"
                            label={localize('submit-registration-form-button-text', 'Submit')}
                            onClick={(e) => submit(e)}
                        />
                            
                    </Show>
                </Show>
            </form>

            {/* Privacy Policy Modal - shows policy based on selected country and user type */}
            <PrivacyPolicyModal
                visible={showPrivacyPolicyModal()}
                onClose={() => setShowPrivacyPolicyModal(false)}
                userCountryCode={formStore.fields.country}
                userType={isHcpUserType() ? 'hcp' : 'patient'}
                title={privacyLinkText()}
                contentType="privacy"
            />

            {/* Marketing Consent Modal - shows marketing content based on selected country and user type */}
            <PrivacyPolicyModal
                visible={showMarketingModal()}
                onClose={() => setShowMarketingModal(false)}
                userCountryCode={formStore.fields.country}
                userType={isHcpUserType() ? 'hcp' : 'patient'}
                title={marketingLinkText()}
                contentType="marketing"
            />
        </ErrorCatcher>
    );
};
